import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Car, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { driverAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const DriverSignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { login, user: authedUser, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  // True when an existing logged-in user is adding the driver role to
  // their account. We hide email/password and skip the auto-login step.
  const isAddRoleMode = !!authedUser;
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    city: '',
    vehicleType: 'sedan',
    plate: '',
    licenseNumber: '',
    dateOfBirth: ''
  });

  // Prefill name and DOB from the authed user in add-role mode.
  useEffect(() => {
    if (isAddRoleMode && authedUser?.name && !form.fullName) {
      setForm(prev => ({
        ...prev,
        fullName: authedUser.name,
        dateOfBirth: prev.dateOfBirth || authedUser.dateOfBirth || ''
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddRoleMode]);

  const ageFromDob = (dobStr) => {
    const dob = new Date(dobStr);
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age;
  };

  // Capture ?ref=<code> from the URL on mount so a driver who landed here
  // via a referral link keeps the attribution all the way through signup.
  // Persists to localStorage under the same key SignupPage uses, so the
  // ref carries across the SPA even if the user wanders to other pages.
  useEffect(() => {
    const refCode = new URLSearchParams(location.search).get('ref');
    if (refCode) {
      localStorage.setItem('bc_ref_code', refCode.trim().toUpperCase());
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // In add-role mode skip password validation entirely — the backend
    // will ignore body email/password when the request is authed.
    if (!isAddRoleMode) {
      if (form.password !== form.confirmPassword) {
        toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
        return;
      }
      if (!form.fullName || !form.email || !form.password || !form.phone || !form.city || !form.vehicleType) {
        toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
        return;
      }
    } else {
      if (!form.fullName || !form.phone || !form.city || !form.vehicleType) {
        toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
        return;
      }
    }

    // Age gate — 18+ required to drive with Ariv (enforced server-side too).
    if (!form.dateOfBirth) {
      toast({ title: 'Date of birth required', description: 'You must be 18 or older to register as a driver.', variant: 'destructive' });
      return;
    }
    const age = ageFromDob(form.dateOfBirth);
    if (age === null || age < 18) {
      toast({ title: 'Not eligible', description: 'You must be 18 or older to register as a driver.', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await driverAPI.register({
        fullName: form.fullName,
        // Email + password are ignored server-side when the request is
        // authed; we still send placeholders so the Pydantic shape
        // validates on the wire.
        email: isAddRoleMode ? authedUser.email : form.email,
        password: isAddRoleMode ? 'addrole-placeholder-ignored' : form.password,
        phone: form.phone,
        city: form.city,
        vehicleType: form.vehicleType,
        plate: form.plate || null,
        licenseNumber: form.licenseNumber || null,
        dateOfBirth: form.dateOfBirth,
        referredBy: localStorage.getItem('bc_ref_code') || undefined
      });

      if (isAddRoleMode) {
        toast({ title: 'Driver role added', description: 'Welcome to the driver dashboard.' });
        try { await refreshUser?.(); } catch (_) {}
      } else {
        toast({ title: 'Success', description: 'Driver account created! Logging you in...' });
        await login(form.email, form.password);
      }
      navigate('/driver/dashboard');
    } catch (error) {
      toast({
        title: 'Registration Failed',
        description: error.response?.data?.detail || 'Could not register driver',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute left-4 top-4"
            onClick={() => navigate('/')}
          >
            <ArrowLeft size={20} />
          </Button>
          <div className="mx-auto mb-4 p-3 bg-[#1F3A68] rounded-full w-fit">
            <Car size={32} className="text-white" />
          </div>
          <CardTitle className="text-2xl text-[#1F3A68]">
            {isAddRoleMode ? 'Add Driver role' : 'Driver Signup'}
          </CardTitle>
          <CardDescription>Join Ariv's taxi service network</CardDescription>
        </CardHeader>
        <CardContent>
          {isAddRoleMode && (
            <div className="mb-4 p-3 bg-[#1F3A68]/5 border border-[#1F3A68]/20 rounded-lg text-sm text-[#1F3A68]">
              <p className="font-semibold">Adding Driver role to your existing account</p>
              <p className="text-xs text-[#1F3A68]/80 mt-0.5 break-all">Linked to {authedUser?.email}.</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={form.fullName}
                onChange={e => setForm(p => ({...p, fullName: e.target.value}))}
                placeholder="John Smith"
                data-testid="driver-name-input"
              />
            </div>

            {isAddRoleMode ? (
              <div>
                <Label>Phone *</Label>
                <Input
                  value={form.phone}
                  onChange={e => setForm(p => ({...p, phone: e.target.value}))}
                  placeholder="+44 123 456 7890"
                  data-testid="driver-phone-input"
                />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={e => setForm(p => ({...p, email: e.target.value}))}
                      placeholder="driver@example.com"
                      data-testid="driver-email-input"
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      value={form.phone}
                      onChange={e => setForm(p => ({...p, phone: e.target.value}))}
                      placeholder="+44 123 456 7890"
                      data-testid="driver-phone-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      value={form.password}
                      onChange={e => setForm(p => ({...p, password: e.target.value}))}
                      placeholder="••••••••"
                      data-testid="driver-password-input"
                    />
                  </div>
                  <div>
                    <Label>Confirm Password *</Label>
                    <Input
                      type="password"
                      value={form.confirmPassword}
                      onChange={e => setForm(p => ({...p, confirmPassword: e.target.value}))}
                      placeholder="••••••••"
                      data-testid="driver-confirm-password-input"
                    />
                  </div>
                </div>
              </>
            )}
            
            <div>
              <Label>Date of Birth *</Label>
              <Input
                type="date"
                value={form.dateOfBirth}
                onChange={e => setForm(p => ({...p, dateOfBirth: e.target.value}))}
                max={new Date().toISOString().split('T')[0]}
                data-testid="driver-dob-input"
              />
              <p className="text-xs text-gray-500 mt-1">You must be 18 or older to drive with Ariv.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City / Service Area *</Label>
                <Input 
                  value={form.city}
                  onChange={e => setForm(p => ({...p, city: e.target.value}))}
                  placeholder="London"
                  data-testid="driver-city-input"
                />
              </div>
              <div>
                <Label>Vehicle Type *</Label>
                <select 
                  value={form.vehicleType}
                  onChange={e => setForm(p => ({...p, vehicleType: e.target.value}))}
                  className="w-full h-10 px-3 border border-gray-200 rounded-md"
                  data-testid="driver-vehicle-input"
                >
                  <option value="sedan">Sedan</option>
                  <option value="suv">SUV</option>
                  <option value="van">Van</option>
                  <option value="minibus">Minibus</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>License Plate (Optional)</Label>
                <Input 
                  value={form.plate}
                  onChange={e => setForm(p => ({...p, plate: e.target.value}))}
                  placeholder="AB12 CDE"
                  data-testid="driver-plate-input"
                />
              </div>
              <div>
                <Label>License Number (Optional)</Label>
                <Input 
                  value={form.licenseNumber}
                  onChange={e => setForm(p => ({...p, licenseNumber: e.target.value}))}
                  placeholder="SMITH123456AB7CD"
                  data-testid="driver-license-input"
                />
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <input type="checkbox" required className="w-4 h-4 rounded border-gray-300 mt-0.5" />
              <span className="text-xs text-gray-600">
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Terms & Conditions</a>
                {' '}and{' '}
                <a href="/terms#data-protection" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Privacy Policy</a>,
                including the Taxi Driver Terms (Section 8) and Zego insurance deduction (&pound;0.35/active hour). I confirm I hold a valid PHV or hackney carriage licence.
              </span>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90"
              disabled={loading}
              data-testid="driver-signup-btn"
            >
              {loading ? 'Registering...' : 'Register as Driver'}
            </Button>
            
            <p className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-[#1F3A68] font-medium hover:underline">
                Login here
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default DriverSignupPage;
